import { useBrand } from 'hooks/useBrand'
import { setUser } from 'hooks/useSessionUser'
import { useTokenApi } from 'hooks/useTokenApi'
import { Loader2, UserRound } from 'lucide-react'
import { FormEvent, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import api, { defaultsHeadersAxios } from 'services/api'
import { getSessionCartId, markSessionCartResolved } from 'utils/anonymousCart'
import { clearMask, dateMask, formatCpf, formatPhone } from 'utils/formatters'
import { getResumeSessionIdFromSearch } from '../../InstantConsultationResumeHandler'

type TRegisterOrRetrieveResponse = {
  success: boolean
  token?: string
  user: { id: number; name: string; email: string }
}

type IdentificationStepProps = {
  onComplete: () => void
}

/**
 * Identificação do fluxo Consulta na Hora whitelabel (Vikura/Remederi).
 * Sem tela de login: os dados do paciente criam/recuperam o usuário via
 * register-or-retrieve (com origin da marca — o deal nasce no backend) e o
 * token retornado autentica a sessão de forma transparente. Exibida em toda
 * visita, mesmo com token salvo (mesma regra do agendamento branded).
 */
export function IdentificationStep({ onComplete }: IdentificationStepProps) {
  const { brand, config } = useBrand()
  const { setTokenApi } = useTokenApi()
  const location = useLocation()
  // Link de retomada (?session=): a pessoa já fez triagem e pagou — confirmar os
  // dados só reautentica, então o texto não deve prometer triagem de novo.
  const isResuming = getResumeSessionIdFromSearch(location.search) !== null
  const brandedOrigin = config.scheduling.origin ?? (brand !== 'cannect' ? brand : null)

  const [cpf, setCpf] = useState('')
  const [name, setName] = useState('')
  const [birthday, setBirthday] = useState('')
  const [email, setEmail] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()

    if (!cpf || !name || !birthday || !email || !whatsapp) {
      toast.error('Preencha todos os campos para continuar.')
      return
    }

    setLoading(true)
    try {
      const sessionCartId = getSessionCartId()
      const response = await api.post<TRegisterOrRetrieveResponse>('/register-or-retrieve?generatePassword=true', {
        name,
        email,
        birthday,
        cpf: clearMask(cpf),
        nationality: 'brasileira',
        whatsapp: clearMask(whatsapp),
        registration_type: 1,
        ...(brandedOrigin ? { origin: brandedOrigin } : {}),
        ...(sessionCartId ? { session_cart_id: sessionCartId } : {})
      })
      if (sessionCartId) {
        markSessionCartResolved(sessionCartId)
      }

      const { user, token } = response.data

      if (!token) {
        toast.error('Não foi possível iniciar sua sessão. Tente novamente.')
        return
      }

      setTokenApi(token)
      defaultsHeadersAxios(token)
      await setUser(user)
      onComplete()
    } catch {
      toast.error('Houve um erro ao salvar seus dados. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const inputClass =
    'h-12 w-full rounded-lg border border-solid border-gray-200 bg-white px-4 text-sm text-gray-700 focus:border-primary-700 focus:outline-none'

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8">
      <div className="mb-8 flex flex-col items-center gap-3 text-center">
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: 'var(--app-green-soft, rgba(33, 151, 110, 0.12))' }}>
          <UserRound className="h-8 w-8 text-primary-700" />
        </div>
        <h1 className="font-spartan text-2xl font-bold text-gray-800 md:text-3xl">
          {isResuming ? (
            <>
              Retomar <span className="text-primary-700">sua consulta</span>
            </>
          ) : (
            <>
              Consulta <span className="text-primary-700">na hora</span>
            </>
          )}
        </h1>
        <p className="max-w-md text-sm text-gray-500 md:text-base">
          {isResuming
            ? 'Confirme seus dados para voltar à sua consulta. Você retorna direto para a fila, sem refazer a triagem.'
            : 'Informe seus dados para começar. Em seguida você responde uma triagem rápida e já entra na fila para falar com um especialista.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3 rounded-2xl bg-white p-6 shadow-sm">
        <label className="text-sm font-medium text-gray-600" htmlFor="instant-cpf">
          CPF *
          <input
            id="instant-cpf"
            className={inputClass}
            placeholder="000.000.000-00"
            value={cpf}
            maxLength={14}
            onChange={(e) => setCpf(formatCpf(e.target.value))}
          />
        </label>
        <label className="text-sm font-medium text-gray-600" htmlFor="instant-name">
          Nome completo *
          <input
            id="instant-name"
            className={inputClass}
            placeholder="Seu nome completo"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="text-sm font-medium text-gray-600" htmlFor="instant-birthday">
          Data de nascimento *
          <input
            id="instant-birthday"
            className={inputClass}
            placeholder="dd/mm/aaaa"
            value={birthday}
            maxLength={10}
            onChange={(e) => setBirthday(dateMask(e.target.value))}
          />
        </label>
        <label className="text-sm font-medium text-gray-600" htmlFor="instant-email">
          E-mail *
          <input
            id="instant-email"
            className={inputClass}
            type="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="text-sm font-medium text-gray-600" htmlFor="instant-whatsapp">
          Telefone *
          <input
            id="instant-whatsapp"
            className={inputClass}
            placeholder="(00) 00000-0000"
            value={whatsapp}
            maxLength={15}
            onChange={(e) => setWhatsapp(formatPhone(e.target.value))}
          />
        </label>

        <button
          type="submit"
          disabled={loading}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border-none bg-primary-700 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-primary-800 disabled:opacity-60">
          {loading && <Loader2 className="h-5 w-5 animate-spin" />}
          {isResuming ? 'Voltar para a consulta' : 'Salvar e continuar'}
        </button>
      </form>
    </div>
  )
}
